import React from 'react';
import { Check, Lock, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
/**
 * Tile in the design picker. Renders a tiny live preview of the design's
 * tokens so the user can read the theme at a glance without us shipping a
 * static SVG per design (those land later as polish).
 */
export function PageDesignCard({ design, selected, locked, onPick, onDelete }) {
    const { t } = useTranslation('campaign');
    const colors = design.tokens.colors ?? {};
    const layout = design.tokens.layout ?? {};
    const typography = design.tokens.typography ?? {};
    const isWorkspace = design.source === 'workspace';
    const previewStyle = {
        background: colors.background ?? '#fff',
        color: colors.foreground ?? '#000',
        borderRadius: layout.cardRadius ?? '0.75rem',
        fontFamily: typography.bodyFont ?? 'system-ui',
    };
    // Lightweight static hint of the animated background pattern. The full
    // animation only runs on the rendered public page (Twig template) — here
    // we surface a tinted gradient so the picker reads the chosen pattern.
    const bgPattern = layout.backgroundPattern ?? 'none';
    const primary = colors.primary ?? '#000';
    const accent = colors.accent ?? colors.primary ?? '#000';
    const patternOverlayStyle = (() => {
        switch (bgPattern) {
            case 'gradient-flow':
                return { background: `linear-gradient(135deg, ${primary}33, transparent 50%, ${accent}33)` };
            case 'aurora':
                return {
                    background: `radial-gradient(circle at 20% 20%, ${primary}55, transparent 55%), radial-gradient(circle at 80% 80%, ${accent}55, transparent 55%)`,
                };
            case 'mesh':
                return {
                    background: `radial-gradient(at 25% 30%, ${primary}55, transparent 55%), radial-gradient(at 75% 70%, ${accent}55, transparent 55%)`,
                };
            case 'grid':
                return {
                    backgroundImage: `linear-gradient(${primary}33 1px, transparent 1px), linear-gradient(90deg, ${primary}33 1px, transparent 1px)`,
                    backgroundSize: '14px 14px',
                };
            case 'bokeh':
                return {
                    background: `radial-gradient(circle at 25% 35%, ${primary}66, transparent 35%), radial-gradient(circle at 70% 70%, ${accent}66, transparent 35%)`,
                };
            case 'waves':
                return {
                    background: `radial-gradient(ellipse at 50% 20%, ${primary}44, transparent 60%), radial-gradient(ellipse at 50% 90%, ${accent}44, transparent 60%)`,
                };
            case 'petals':
                return {
                    background: `radial-gradient(circle at 30% 30%, ${primary}33 6px, transparent 7px), radial-gradient(circle at 70% 60%, ${accent}33 4px, transparent 5px)`,
                };
            default:
                return null;
        }
    })();
    const swatchStyle = {
        background: colors.primary ?? '#000',
        color: colors.primaryForeground ?? '#fff',
        fontFamily: typography.headingFont ?? typography.bodyFont ?? 'system-ui',
        fontWeight: Number(typography.headingWeight ?? '600'),
    };
    return (<div className={cn('group relative flex flex-col overflow-hidden rounded-xl border bg-background text-left transition-all', 'focus-within:ring-2 focus-within:ring-primary', selected && !locked
            ? 'border-primary ring-1 ring-primary shadow-sm'
            : 'border-border hover:border-primary/60 hover:shadow-md', locked && 'opacity-70')}>
            {isWorkspace && !locked && (<span className="absolute left-2 top-2 z-10 inline-flex items-center rounded-full bg-primary/90 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-primary-foreground backdrop-blur-sm">
                    {t('page_design.yours_badge', { defaultValue: 'Yours' })}
                </span>)}
            {onDelete && (<button type="button" onClick={(e) => {
                e.stopPropagation();
                onDelete();
            }} aria-label={t('page_design.delete_aria', { defaultValue: 'Delete design' })} className="absolute right-2 top-2 z-20 inline-flex h-6 w-6 items-center justify-center rounded-full border border-border bg-background/90 text-muted-foreground hover:border-destructive hover:text-destructive backdrop-blur-sm">
                    <Trash2 className="h-3 w-3"/>
                </button>)}
            <button type="button" onClick={() => !locked && onPick(design)} disabled={locked} aria-pressed={selected} aria-disabled={locked} className={cn('flex flex-1 flex-col text-left', 'focus-visible:outline-none', locked ? 'cursor-not-allowed' : 'cursor-pointer')}>
                {locked && (<span className="absolute right-2 top-2 z-10 inline-flex items-center gap-1 rounded-full border bg-muted/90 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground backdrop-blur-sm">
                        <Lock className="h-2.5 w-2.5"/>
                        {t('mode_picker.lock_badge')}
                    </span>)}
                {selected && !locked && (<span className="absolute right-2 top-2 z-10 inline-flex h-6 w-6 items-center justify-center rounded-full bg-primary text-primary-foreground">
                        <Check className="h-3.5 w-3.5"/>
                    </span>)}

                {/* Live preview built from tokens — no static asset needed for v1. */}
                <div className="relative aspect-[5/3] w-full overflow-hidden p-3" style={previewStyle}>
                    {patternOverlayStyle && (<div className="pointer-events-none absolute inset-0 opacity-70" style={patternOverlayStyle} aria-hidden="true"/>)}
                    <div className="relative flex h-full w-full flex-col gap-2 rounded-md p-2.5" style={{ background: colors.card ?? colors.background ?? '#fff' }}>
                        <div className="h-1.5 w-12 rounded-full" style={{ background: colors.mutedForeground ?? '#999', opacity: 0.5 }}/>
                        <div className="text-sm leading-tight" style={{
            color: colors.cardForeground ?? colors.foreground ?? '#000',
            fontFamily: typography.headingFont ?? 'system-ui',
            fontWeight: Number(typography.headingWeight ?? '600'),
        }}>
                            {design.name}
                        </div>
                        <div className="mt-auto flex items-center gap-1.5">
                            <span className="inline-flex items-center rounded px-2 py-0.5 text-[10px] font-medium" style={swatchStyle}>
                                CTA
                            </span>
                            <span className="h-1.5 w-1.5 rounded-full" style={{ background: colors.accent ?? colors.muted ?? '#eee' }}/>
                        </div>
                    </div>
                </div>

                <div className="flex flex-1 flex-col gap-1 p-3">
                    <h3 className="text-sm font-semibold leading-tight">{design.name}</h3>
                    {design.description && (<p className="text-xs text-muted-foreground leading-snug line-clamp-2">{design.description}</p>)}
                </div>
            </button>
        </div>);
}
